import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Store.
import { bannerErrors } from 'app/client/store/actions/errorActions';

import Alert from '../Alert';

/**
 * Render an array of errors.
 *
 * @param {array} errors An array containing objects of errors.
 *
 * @returns {Element|null} The rendered errors.
 */
const ApiError = ({ errors, withBannerErrors = false }) => {
  const { t, i18n } = useTranslation();

  // The layout already shows these errors in its banner.
  const visible = withBannerErrors ? errors : errors.filter(({ status }) => !bannerErrors[status]);

  if (visible.length === 0) {
    return null;
  }

  // Field-level details are left alone: they are more specific than our copy.
  const translate = ({ status, title, detail, variables }) => {
    if (bannerErrors[status]) {
      return t(`${bannerErrors[status]}.message`);
    }

    const keys = [
      `core:error.api.${title}`,
      `core:error.api.${detail}`,
      `core:error.api.rule.${variables?.rule}`,
    ];
    const known = keys.find((key) => i18n.exists(key));

    return known ? t(known) : detail || t('core:error.exception.message');
  };

  return (
    <Alert error>
      {visible.map((error, index) => (
        <div key={`error-${index.toString()}`}>
          <strong>{t('core:error.exception.title')}</strong>
          <br />
          <small>{translate(error)}</small>
        </div>
      ))}
    </Alert>
  );
};

ApiError.propTypes = {
  errors: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      detail: PropTypes.string,
      title: PropTypes.string,
      variables: PropTypes.shape({
        rule: PropTypes.string,
      }),
    }),
  ).isRequired,
  // For a modal, which covers the banner of the layout.
  withBannerErrors: PropTypes.bool,
};

export default ApiError;
